import { useState, useEffect, useRef } from "react"
import { useSelector } from "react-redux"
import axios from "axios"
import { RootState } from "../store/store"
import { userProfileGuardCheck } from "../store/slices/user_profile.slice"
import { withPrefix } from "gatsby"
import { events } from "../utils/heap/tracking_events"

type MetadataResponse = {
  result: {
    metadata: {
      salesforceCaseId?: string
      salesforceCaseIdUpdatedAt?: string
      [key: string]: any
    }
  }
  statusCode: number
  message: string
}

const POLLING_INTERVAL = 6000
let MAX_POLLING_TIME = 8400000 // 140 minutes in milliseconds

export const useUserMetadataSfCaseIdPolling = () => {
  // Use Ref for interval to avoid re-renders and closure staleness issues
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const startTimeRef = useRef<number | null>(null) // Track when polling started

  const shouldPollUserMetadata = useSelector(
    (state: RootState) => state.metaData.shouldPollUserMetadata
  )
  const userProfile = userProfileGuardCheck(
    useSelector((state: RootState) => state.userProfile)
  )
  const userId = userProfile?.userData?.id

  const [metadata, setMetadata] = useState<
    MetadataResponse["result"]["metadata"] | null
  >(null)
  const [connectionStatus, setConnectionStatus] = useState("Connecting...")
  const [hasFiredUsabillaLive, setHasFiredUsabillaLive] = useState(false)

  useEffect(() => {
    startTimeRef.current = Date.now()
  }, [])

  useEffect(() => {
    // check all of our start conditions to make sure we can start polling
    if (!shouldPollUserMetadata || !userId || hasFiredUsabillaLive) {
      if (hasFiredUsabillaLive) {
        setConnectionStatus("Disconnected")
      }

      return
    }

    const fetchData = async () => {
      // Check if the max polling time has been reached and don't poll if it has
      if (
        startTimeRef.current &&
        Date.now() - startTimeRef.current > MAX_POLLING_TIME
      ) {
        if (process.env.GATSBY_DEBUG === "true") {
          console.log("Max polling time reached. Stopping.")
        }

        return
      }

      // Check if the document is hidden or out of focus and don't poll if it is
      if (typeof document !== "undefined") {
        if (process.env.GATSBY_DEBUG === "true") {
          console.log("document.visibilityState", {
            visibilityState: document.visibilityState,
            hidden: document.hidden,
            hasFocus: document.hasFocus()
          })
        }

        if (
          document.visibilityState === "hidden" ||
          document.hidden ||
          !document.hasFocus()
        ) {
          if (process.env.GATSBY_DEBUG === "true") {
            console.log(
              "document is hidden or out of focus, returning without polling"
            )
          }

          // if we are delaying polling, increase the max polling time by 5 seconds so a user gets 20 minutes of polling time while actively using the site
          if (process.env.GATSBY_DEBUG === "true") {
            console.log("MAX_POLLING_TIME:", MAX_POLLING_TIME)
          }

          MAX_POLLING_TIME = MAX_POLLING_TIME + POLLING_INTERVAL

          if (process.env.GATSBY_DEBUG === "true") {
            console.log("extending polling time to:", MAX_POLLING_TIME)
          }

          return
        }
      }

      try {
        const TARGET_URL = withPrefix(`/api/user/metadata/${userId}`)

        const response = await axios.get<MetadataResponse>(TARGET_URL, {
          timeout: 30000,
          headers: { "Cache-Control": "no-cache" }
        })

        if (response.data.statusCode === 200) {
          const newMetadata = response.data.result.metadata

          // Only update if we actually have the ID we are looking for
          if (newMetadata?.salesforceCaseId) {
            setMetadata(newMetadata)
            setConnectionStatus("Disconnecting...")
            // Clear interval immediately on success
            if (intervalRef.current) {
              clearInterval(intervalRef.current)
            }
          } else {
            setMetadata(null)
            setConnectionStatus("Retrying...")
          }
        } else {
          setConnectionStatus("Retrying...")
        }
      } catch (error) {
        if (process.env.GATSBY_DEBUG === "true") {
          console.error("Polling Error:", error)
        }
        setConnectionStatus("Error...")
      }
    }

    // Call immediately
    fetchData()

    intervalRef.current = setInterval(fetchData, POLLING_INTERVAL)

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
    // Removed connectionStatus from dependency array to prevent infinite loop
  }, [userId, shouldPollUserMetadata, hasFiredUsabillaLive])

  // Effect 2: Trigger Usabilla
  useEffect(() => {
    if (metadata?.salesforceCaseIdUpdatedAt) {
      const isWithin5Minutes = () => {
        try {
          if (!metadata.salesforceCaseIdUpdatedAt) return false

          // 1. Get the target date string
          const dateString = metadata.salesforceCaseIdUpdatedAt

          // 2. Convert both times to Milliseconds
          // The 'Z' in the string indicates UTC, which new Date() handles automatically
          const targetTime = new Date(dateString).getTime()
          const currentTime = new Date().getTime()

          // 3. Calculate difference in milliseconds
          const differenceInMs = currentTime - targetTime

          // 4. Define 5 minutes in milliseconds (5 * 60 seconds * 1000 ms)
          const fiveMinutesInMs = 5 * 60 * 1000

          // 5. Check if the difference is valid (positive) and within 5 minutes
          if (differenceInMs >= 0 && differenceInMs <= fiveMinutesInMs) {
            return true
          } else {
            process.env.GATSBY_DEBUG === "true" &&
              console.log(
                "No action taken: Date is older than 5 minutes or in the future."
              )
            return false
          }
        } catch (error) {
          console.error("Error parsing date:", error)
          return false
        }
      }

      if (
        isWithin5Minutes() &&
        metadata?.salesforceCaseId &&
        metadata.salesforceCaseId.length > 0 &&
        !hasFiredUsabillaLive &&
        window?.usabilla_live
      ) {
        if (window?.duet) {
          window.duet.close()
        }

        events.userReceivedCsatSurveyDuringEscalation("Main Layout")

        window.usabilla_live("trigger", "WebChatService.ended")
        window.usabilla_live("data", {
          custom: { CaseId: metadata.salesforceCaseId }
        })

        setHasFiredUsabillaLive(true)
      }
    }
  }, [metadata, hasFiredUsabillaLive])

  return { metadata, connectionStatus }
}
